import React, { useContext, useEffect, useRef, useState } from 'react';
import classnames from 'classnames';
import { filter, pairwise, pluck, share, startWith, switchMap, tap, withLatestFrom, mergeMap } from 'rxjs/operators';
import { BehaviorSubject, merge, Observable, Subject, timer, zip, of, EMPTY } from 'rxjs';
import { Action, Location } from 'history';
import { useDispatch } from 'react-redux';

import { ResolvedComponent, ResolvedUrl } from '../utils/resolve';
import { createDebug } from '../utils/runtimeDebug';
import { scrollToTopEffect, scrollToTopOffsetEffect } from '../runtime/effects/windowScrollTop';

import { AsyncRouterContext, HistoryEvent } from './AsyncRouter';
import { Msg } from './router.register';

const debug = createDebug('AsyncRouter:AsyncLoader');

interface AsyncLoaderProps {
    nav$: Observable<HistoryEvent>;
    complete$: Subject<any>;
    initial: ResolvedComponent | null;
    initialLocation: Location;
    initialAction: Action;
    onlineOutdated$: Observable<{ online: boolean; outdated: boolean }>;
    resolveLocal(
        pathname: string,
        online$: Observable<boolean>,
        outdated$: Observable<boolean>,
    ): Observable<ResolvedComponent>;
}

export const AsyncLoader: React.FC<AsyncLoaderProps> = React.memo((props) => {
    const [resolved, setComponent] = useState<ResolvedComponent | null>(props.initial);
    const [loading, setLoading] = useState(false);
    const { resolveLocal } = props;
    const dispatch = useDispatch();
    const { base$ } = useContext(AsyncRouterContext);

    const initialHistoryEvent = useRef<HistoryEvent>({
        location: props.initialLocation,
        action: props.initialAction,
    });

    useEffect(() => {
        const input: Observable<[[HistoryEvent, HistoryEvent], any]> = props.nav$.pipe(
            tap((x) => debug('incoming history event', x.action, x.location.pathname)),
            tap((x) => dispatch(Msg('AsyncRouter.CHANGE', x))),
            startWith(initialHistoryEvent.current),
            pairwise(),
            withLatestFrom(base$?.current || (new BehaviorSubject<Set<string>>(new Set([])) as any)),
            share(),
        );

        const initialResolvedUrl: ResolvedUrl | null = props.initial && {
            componentName: props.initial.componentName,
            id: props.initial.id,
            urlKey: props.initial.urlKey,
        };

        const componentStream$ = new BehaviorSubject<ResolvedUrl | null>(initialResolvedUrl);

        const differentBase = input.pipe(
            filter((x) => baseFilter(x)),
            tap(() => setLoading(true)),
            switchMap(([[, next]]) => {
                return zip(
                    resolveLocal(
                        next.location.pathname,
                        props.onlineOutdated$.pipe(pluck('online')),
                        props.onlineOutdated$.pipe(pluck('outdated')),
                    ),
                    timer(500),
                ).pipe(
                    mergeMap(([resolvedComponent]) => {
                        if (resolvedComponent.redirectUrl && resolvedComponent.redirectStatusCode) {
                            debug('redirecting', resolvedComponent);
                            window.location.replace(resolvedComponent.redirectUrl);
                            return EMPTY;
                        }
                        return of([next, resolvedComponent] as const);
                    }),
                );
            }),
            tap(([, resolvedComponent]) => debug('resolved component', resolvedComponent)),
            tap(([, resolvedComponent]) => setComponent(resolvedComponent)),
            tap(([, resolvedComponent]) =>
                componentStream$.next({
                    componentName: resolvedComponent.componentName,
                    urlKey: resolvedComponent.urlKey,
                    id: resolvedComponent.id,
                }),
            ),
            tap(([next]) => maybeScrollTop(next)),
        );

        /**
         * Convert same-base navigation events into ones
         * that can still be tracked
         */
        const sameBase = input.pipe(
            filter((x) => !baseFilter(x)),
            tap(([[, next]]) => maybeScrollTop(next)),
            withLatestFrom(componentStream$),
            tap((x) => {
                const [[, next]] = x[0];
                if (x[1]) {
                    const { componentName, id } = x[1];
                    const nextC = {
                        componentName,
                        id,
                        urlKey: next.location.pathname,
                    };
                    componentStream$.next(nextC);
                }
            }),
        );

        /**
         *
         */
        const routerResolved$ = merge(differentBase, sameBase).pipe(
            tap(() => dispatch(Msg('AsyncRouter.Resolved'))),
            share(),
        );

        /**
         * These are events that mark when the current page is 'resolved'
         */
        const componentReady$ = props.complete$.pipe(
            withLatestFrom(routerResolved$, componentStream$),
            tap(([, , cmp]) => {
                if (cmp) {
                    dispatch(Msg('AsyncRouter.Complete', cmp));
                }
                setLoading(false);
            }),
        );

        const subs = merge(routerResolved$, componentReady$).subscribe();

        return () => {
            subs.unsubscribe();
        };
    }, [
        props.complete$,
        props.initial,
        props.nav$,
        initialHistoryEvent,
        setComponent,
        dispatch,
        resolveLocal,
        base$,
        props.onlineOutdated$,
    ]);

    return (
        <div
            className={classnames({
                ['opacity-0']: loading,
                ['opacity-1']: !loading,
            })}
        >
            {resolved && resolved.Cmp && (
                <resolved.Cmp id={resolved.id} key={resolved.urlKey} pathname={resolved.urlKey} />
            )}
        </div>
    );
});

function baseFilter([[prev, next], excludeSet]) {
    const p1 = prev.location.pathname;
    const p2 = next.location.pathname;
    const [seg1] = p1.slice(1).split('/');
    if (isSameBase([prev, next]) && (excludeSet as Set<string>).has(seg1)) {
        debug('BAILING (BASE) since bases are the same');
        return false;
    }
    if (p1 === p2) {
        debug('BAILING since paths match');
        return false;
    }
    return true;
}

function isSameBase([prev, next]: [HistoryEvent, HistoryEvent]): boolean {
    const p1 = prev.location.pathname;
    const p2 = next.location.pathname;
    const [seg1] = p1.slice(1).split('/');
    const [seg2] = p2.slice(1).split('/');
    return seg1 === seg2;
}

function maybeScrollTop(next: HistoryEvent): undefined | void {
    if (next.action === 'REPLACE') {
        return undefined;
    }

    /**
     * did this location event have a scroll param?
     */
    const withScroll = hasScrollParam(next.location.state);
    if (withScroll) {
        const { offset = 0, scrollTo } = withScroll;
        if (offset) {
            return scrollToTopOffsetEffect(300, scrollTo, offset);
        } else {
            return scrollToTopEffect(300, scrollTo);
        }
    }

    /**
     * Fallback is to just scroll to top immediately
     */
    window.scrollTo(0, 0);
}

function hasScrollParam(input: any): { scrollTo: string; offset?: number } | undefined {
    if (input && typeof input.scrollTo === 'string') {
        return input;
    }
    return undefined;
}
